/**
 * 后台：独享容器（唯一容器产品）— 实例（含旧版 host-manager 容器）/ 节点 / 套餐 / 模板 / 待开通 / WARP / 设置。
 * 运行时默认 Kata；runc 仅管理员按节点或套餐显式设置。
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
    Alert, Button, Card, Descriptions, Drawer, Form, Input, InputNumber, Modal, Popconfirm, Select, Space, Switch, Table, Tabs, Tag, Tooltip,
    Typography, message,
} from 'antd';
import { useNavigate, useParams } from 'react-router-dom';
import { adminPath } from '../../../shared/config/paths';
import { CodeOutlined, FolderOpenOutlined, ReloadOutlined, SearchOutlined } from '@ant-design/icons';
import PageHeader from '../../../shared/components/PageHeader';
import { useTranslation } from '../../../shared/i18n';
import { containerAdminApi as api, containerBackupApi, isOk, errMsg } from '../../../shared/services/containerService';
import AutoBackupCard from '../../../shared/components/evm/vmdetail/AutoBackupCard';
import {
    CONTAINER_STATUS, ContainerStatusTag, PowerButtons, UsageBlock, LogsPanel, HealthDot, PortTable, specText, needsPoll, arr,
} from '../../../shared/components/container/ContainerParts';
import { ContainerTerminal } from '../../../shared/components/container/ContainerTools';
import ContainerFiles from '../../../shared/components/container/ContainerFiles';
import EgressCard from '../../../shared/components/container/EgressCard';
import AdminContainerExtras from './AdminContainerExtras';

const { Text } = Typography;
const backupApi = containerBackupApi('admin');
const CYCLES = { monthly: '月付', quarterly: '季付', annually: '年付' };
const RUNC_WARNING = 'runc 无虚拟机隔离，仅限可信场景';
const MIG_STAGE = { creating: ['创建中', 'processing'], importing: ['导入中', 'processing'], done: ['已完成', 'success'], failed: ['失败', 'error'] };

const RuntimeTag = ({ rt }) => (rt === 'runc' ? <Tag color="red">runc</Tag> : null);

/** 运行时选择：选 runc 前弹窗确认警告。 */
const RuntimeSelect = ({ value, warning, loading, onChange }) => {
    const { t } = useTranslation();
    const pick = (v) => {
        if (v === value) return;
        if (v !== 'runc') { onChange(v); return; }
        Modal.confirm({ title: t('Switch to runc?'), content: t(warning || RUNC_WARNING), okButtonProps: { danger: true }, onOk: () => onChange(v) });
    };
    return (
        <Select size="small" style={{ width: 90 }} value={value || 'kata'} loading={loading} onChange={pick}
            options={[{ value: 'kata', label: 'Kata' }, { value: 'runc', label: 'runc' }]} />
    );
};

const MigrationState = ({ m }) => {
    const { t } = useTranslation();
    if (!m || !m.stage) return null;
    const [l, color] = MIG_STAGE[m.stage] || [m.stage, 'default'];
    return (
        <Space wrap>
            <Tag color={color}>{t(l)}</Tag>
            {m.container_id && <Text type="secondary">{t('Container')} #{m.container_id}</Text>}
            {m.error && <Text type="danger">{m.error}</Text>}
        </Space>
    );
};

/** 旧版（host-manager）容器：信息 + 「迁移到 Kata」（创建同用户同订阅的 Kata 容器，复制 /var/www/html，切换订阅类型，停旧容器）。 */
const LegacyDrawer = ({ row, onClose, onChanged }) => {
    const { t } = useTranslation();
    const [m, setM] = useState(null);
    const [nodes, setNodes] = useState([]);
    const [nodeId, setNodeId] = useState(null);
    const [busy, setBusy] = useState(false);
    const timer = useRef(null);
    const id = row?.id;

    const poll = useCallback(async () => {
        if (!id) return;
        const r = await api.legacyMigration(id);
        if (isOk(r)) setM(r.data || null);
    }, [id]);
    useEffect(() => {
        setM(row?.migration || null);
        if (!id) return;
        poll();
        api.nodes().then((r) => { if (isOk(r)) setNodes(arr(r.data?.list).filter((n) => n.kata_capable)); });
    }, [id, row, poll]);
    useEffect(() => {
        clearTimeout(timer.current);
        if (m && ['creating', 'importing'].includes(m.stage)) timer.current = setTimeout(poll, 3000);
        if (m && m.stage === 'done') onChanged();
        return () => clearTimeout(timer.current);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [m, poll]);

    const migrate = async () => {
        setBusy(true);
        const r = await api.migrateLegacy(id, nodeId);
        setBusy(false);
        if (isOk(r)) { message.success(t('Migration started')); poll(); } else message.error(errMsg(r, t('Operation failed')));
    };

    const running = m && ['creating', 'importing'].includes(m.stage);
    return (
        <Drawer open={!!row} onClose={onClose} width={640} destroyOnClose
            title={row ? <Space>{row.name}<Tag>{t('Legacy')}</Tag><ContainerStatusTag status={row.status} /></Space> : ''}>
            {row && (
                <Space direction="vertical" size={16} style={{ width: '100%' }}>
                    <Descriptions size="small" column={2} bordered>
                        <Descriptions.Item label="ID">{row.id}</Descriptions.Item>
                        <Descriptions.Item label="UID">{row.uid}</Descriptions.Item>
                        <Descriptions.Item label={t('Node')}>{row.node_name || row.node_id || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Runtime')}>{row.runtime || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Subscription')}>{row.subscription_id || '-'}{row.subscription_status ? ` (${row.subscription_status})` : ''}</Descriptions.Item>
                        <Descriptions.Item label={t('Expires')}>{row.expires_at ? String(row.expires_at).slice(0, 10) : '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Configuration')}>{specText(row)}</Descriptions.Item>
                        <Descriptions.Item label={t('Private IP')}>{row.private_ip || '-'}</Descriptions.Item>
                        <Descriptions.Item label={t('Image')} span={2}><Text code>{row.image}</Text></Descriptions.Item>
                    </Descriptions>
                    <Card size="small" title={t('Migrate to Kata')}>
                        <Space direction="vertical" style={{ width: '100%' }}>
                            <MigrationState m={m} />
                            {(!m || m.stage === 'failed') && (
                                <Space wrap>
                                    <Select allowClear size="small" style={{ width: 200 }} placeholder={t('Auto-select node')} value={nodeId} onChange={setNodeId}
                                        disabled={m?.stage === 'failed'}
                                        options={nodes.map((n) => ({ value: n.id, label: `#${n.id} ${n.name}` }))} />
                                    <Popconfirm title={m?.stage === 'failed' ? t('Retry migration?') : t('Migrate to Kata?')} onConfirm={migrate}>
                                        <Button type="primary" size="small" loading={busy}>{m?.stage === 'failed' ? t('Retry') : t('Migrate to Kata')}</Button>
                                    </Popconfirm>
                                </Space>
                            )}
                            {running && <Text type="secondary">{t('Migrating…')}</Text>}
                        </Space>
                    </Card>
                </Space>
            )}
        </Drawer>
    );
};

const DetailDrawer = ({ id, onClose, onChanged }) => {
    const { t } = useTranslation();
    const [c, setC] = useState(null);
    const load = useCallback(async () => {
        const r = await api.get(id);
        if (isOk(r)) setC(r.data); else message.error(errMsg(r, t('Failed to load')));
    }, [id, t]);
    useEffect(() => { setC(null); if (id) load(); }, [id, load]);
    const fetchLogs = useCallback(() => api.logs(id), [id]);
    const termTicket = useCallback(() => api.terminal(id), [id]);
    const filesTicket = useCallback(() => api.files(id), [id]);
    const [tool, setTool] = useState(null); // 'terminal' | 'files'
    const [tab, setTab] = useState('info');
    const net = c?.network || {};

    return (
        <Drawer open={!!id} onClose={onClose} width={860} destroyOnClose
            title={c ? <Space>{c.label || c.name || `#${c.id}`}<ContainerStatusTag status={c.status} busy={c.busy} /></Space> : `#${id || ''}`}
            extra={c && <Space>
                <Button icon={<CodeOutlined />} onClick={() => setTool('terminal')}>{t('Terminal')}</Button>
                <Button icon={<FolderOpenOutlined />} onClick={() => setTool('files')}>{t('File')}</Button>
                <Button icon={<ReloadOutlined />} onClick={load} />
                <PowerButtons c={c} api={api} type="default" onDone={() => { setTimeout(load, 1500); onChanged(); }} onDeleted={() => { onChanged(); onClose(); }} />
            </Space>}>
            {c && (
                <Tabs activeKey={tab} onChange={setTab} items={[
                    {
                        key: 'info', label: t('Overview'), children: (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                {c.error_msg && <Text type="danger">{c.error_msg}</Text>}
                                <UsageBlock c={c} />
                                <Descriptions size="small" column={2} bordered>
                                    <Descriptions.Item label="UID">{c.uid}</Descriptions.Item>
                                    <Descriptions.Item label={t('Node')}>{c.node_name || c.node_id || '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Image')}><Text code>{c.image}</Text></Descriptions.Item>
                                    <Descriptions.Item label={t('Start command')}>{c.cmd ? <Text code>{c.cmd}</Text> : '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Environment variables')}>{arr(c.env_keys).map((k) => <Tag key={k}>{k}</Tag>)}</Descriptions.Item>
                                    <Descriptions.Item label={t('Agent state')}>{c.state || '-'} / {c.want || '-'}</Descriptions.Item>
                                    <Descriptions.Item label="MAC">{c.mac || '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Subscription')}>{c.subscription_id || '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Expires')}>{c.expires_at || '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Created at')}>{c.created_at || '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Runtime')}>{c.runtime === 'runc' ? <Tag color="red">runc</Tag> : 'Kata'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Cron jobs / services')}>{arr(c.apps?.cron).length} / {arr(c.apps?.services).length}</Descriptions.Item>
                                    {c.migration && <Descriptions.Item label={t('Migrate')} span={2}><MigrationState m={c.migration} /></Descriptions.Item>}
                                </Descriptions>
                            </Space>
                        ),
                    },
                    {
                        key: 'net', label: t('Network'), children: (
                            <Space direction="vertical" size={16} style={{ width: '100%' }}>
                                <Descriptions size="small" column={3} bordered>
                                    <Descriptions.Item label={t('Private IP')}>{net.private_ip || c.private_ip || '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Gateway')}>{net.gateway || '-'}</Descriptions.Item>
                                    <Descriptions.Item label={t('Public IP')}>{net.public_ip || '-'}</Descriptions.Item>
                                </Descriptions>
                                <EgressCard egress={c.egress} onSave={(mode, fallback) => api.egress(c.id, { mode, fallback })} onSaved={load} />
                                <PortTable ports={net.ports} publicIp={net.public_ip} />
                                <div>
                                    <Text type="secondary">{t('Security groups')}: </Text>
                                    {arr(net.security_groups).length ? arr(net.security_groups).map((g) => <Tag key={g.id}>{g.name}</Tag>) : '-'}
                                </div>
                                <Table size="small" rowKey="hostname_id" pagination={false} dataSource={arr(net.domains)}
                                    locale={{ emptyText: t('Nothing bound') }}
                                    columns={[
                                        { title: t('Domains'), dataIndex: 'hostname' },
                                        { title: t('Container port'), dataIndex: 'port' },
                                        { title: t('Health'), dataIndex: 'healthy', render: (v) => <HealthDot healthy={v} /> },
                                    ]} />
                            </Space>
                        ),
                    },
                    { key: 'extras', label: '附加', children: <AdminContainerExtras id={c.id} onChanged={load} /> },
                    { key: 'logs', label: t('Logs'), children: <LogsPanel fetcher={fetchLogs} /> },
                    { key: 'backup', label: t('Backups'), children: <AutoBackupCard id={c.id} api={backupApi} adapter={backupApi} resType="container" active={tab === 'backup'} /> },
                ]} />
            )}
            <Modal open={!!tool} onCancel={() => setTool(null)} footer={null} width={1100} destroyOnClose
                title={`${tool === 'files' ? t('File') : t('Terminal')} · ${c?.label || c?.name || `#${id}`}`}>
                {tool === 'terminal' && <ContainerTerminal fetchTicket={termTicket} />}
                {tool === 'files' && <ContainerFiles fetchTicket={filesTicket} root={c?.files_root} />}
            </Modal>
        </Drawer>
    );
};

const InstancesTab = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [total, setTotal] = useState(0);
    const [loading, setLoading] = useState(false);
    const [q, setQ] = useState({ page: 1, page_size: 20, keyword: '', status: undefined });
    const [kw, setKw] = useState('');
    const [detailId, setDetailId] = useState(null);
    const [legacyRow, setLegacyRow] = useState(null);
    const timer = useRef(null);

    const load = useCallback(async (silent = false) => {
        if (!silent) setLoading(true);
        const params = { page: q.page, page_size: q.page_size };
        if (q.keyword) params.keyword = q.keyword;
        if (q.status) params.status = q.status;
        const r = await api.list(params);
        setLoading(false);
        if (isOk(r)) {
            // 旧版容器排在最前（仅第一页返回）
            setRows([...arr(r.data?.legacy).map((l) => ({ ...l, key: `l${l.id}`, legacy: true })), ...arr(r.data?.list).map((c) => ({ ...c, key: `k${c.id}` }))]);
            setTotal(Number(r.data?.total) || 0);
        } else if (!silent) message.error(errMsg(r, t('Failed to load')));
    }, [q, t]);
    useEffect(() => { load(); }, [load]);
    useEffect(() => {
        clearTimeout(timer.current);
        if (rows.some((x) => !x.legacy && needsPoll(x))) timer.current = setTimeout(() => load(true), 10000);
        return () => clearTimeout(timer.current);
    }, [rows, load]);

    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        {
            title: t('Name'), key: 'name', render: (_, c) => (
                <Space direction="vertical" size={2}>
                    <Space>
                        <Button type="link" size="small" style={{ padding: 0 }} onClick={() => (c.legacy ? setLegacyRow(c) : setDetailId(c.id))}>{c.label || c.name || `#${c.id}`}</Button>
                        <ContainerStatusTag status={c.status} busy={c.busy} />
                        {c.legacy && <Tag>{t('Legacy')}</Tag>}
                        <RuntimeTag rt={c.legacy ? null : c.runtime} />
                        {c.legacy && c.migration?.stage && <MigrationState m={c.migration} />}
                    </Space>
                    <Text type="secondary" style={{ fontSize: 12 }}>{c.image}</Text>
                </Space>
            ),
        },
        { title: 'UID', dataIndex: 'uid', width: 80 },
        { title: t('Node'), key: 'node', render: (_, c) => c.node_name || c.node_id || '-' },
        { title: t('Configuration'), key: 'spec', render: (_, c) => specText(c) },
        { title: t('Private IP'), dataIndex: 'private_ip', render: (v) => v || '-' },
        { title: t('Expires'), dataIndex: 'expires_at', render: (v) => (v ? String(v).slice(0, 10) : '-') },
        {
            title: t('Actions'), key: 'op', width: 230, render: (_, c) => (c.legacy
                ? <Button size="small" type="link" onClick={() => setLegacyRow(c)}>{t('Migrate to Kata')}</Button>
                : (
                <Space size={0}>
                    <PowerButtons c={c} api={api} onDone={() => setTimeout(() => load(true), 1500)} onDeleted={() => load(true)} />
                    <Button size="small" type="link" onClick={() => setDetailId(c.id)}>{t('Details')}</Button>
                </Space>
            )),
        },
    ];

    return (
        <>
            <Space wrap style={{ marginBottom: 12 }}>
                <Input allowClear prefix={<SearchOutlined />} placeholder={t('Name / image / IP')} style={{ width: 240 }}
                    value={kw} onChange={(e) => setKw(e.target.value)}
                    onPressEnter={() => setQ((s) => ({ ...s, page: 1, keyword: kw.trim() }))}
                    onClear={() => setQ((s) => ({ ...s, page: 1, keyword: '' }))} />
                <Select allowClear placeholder={t('Status')} style={{ width: 140 }} value={q.status}
                    onChange={(v) => setQ((s) => ({ ...s, page: 1, status: v }))}
                    options={Object.entries(CONTAINER_STATUS).filter(([k]) => k !== 'provisioning').map(([k, [l]]) => ({ value: k, label: t(l) }))} />
                <Button icon={<ReloadOutlined />} onClick={() => load()} />
            </Space>
            <Table rowKey="key" size="small" columns={columns} dataSource={rows} loading={loading} scroll={{ x: 'max-content' }}
                pagination={{
                    current: q.page, pageSize: q.page_size, total, showSizeChanger: true,
                    onChange: (page, page_size) => setQ((s) => ({ ...s, page, page_size })),
                }} />
            <DetailDrawer id={detailId} onClose={() => setDetailId(null)} onChanged={() => load(true)} />
            <LegacyDrawer row={legacyRow} onClose={() => setLegacyRow(null)} onChanged={() => load(true)} />
        </>
    );
};

const NodesTab = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [overhead, setOverhead] = useState(null);
    const [legacy, setLegacy] = useState([]);
    const [warning, setWarning] = useState(RUNC_WARNING);
    const [loading, setLoading] = useState(false);
    const [edits, setEdits] = useState({});
    const [saving, setSaving] = useState(null);

    const load = useCallback(async () => {
        setLoading(true);
        const r = await api.nodes();
        setLoading(false);
        if (isOk(r)) {
            setRows(arr(r.data?.list)); setOverhead(r.data?.overhead_mb ?? null); setEdits({});
            setLegacy(arr(r.data?.legacy)); if (r.data?.runc_warning) setWarning(r.data.runc_warning);
        } else message.error(errMsg(r, t('Failed to load')));
    }, [t]);
    useEffect(() => { load(); }, [load]);

    const save = async (n, body) => {
        setSaving(n.id);
        const r = await api.updateNode(n.id, body);
        setSaving(null);
        if (isOk(r)) { message.success(t('Saved')); load(); } else message.error(errMsg(r, t('Failed to save')));
    };
    const edit = (id, k, v) => setEdits((s) => ({ ...s, [id]: { ...(s[id] || {}), [k]: v } }));

    const columns = [
        { title: t('Node'), key: 'name', render: (_, n) => <Space direction="vertical" size={0}><span>{n.name}</span><Text type="secondary" style={{ fontSize: 12 }}>{n.ip}</Text></Space> },
        {
            title: 'Kata', key: 'kata', render: (_, n) => (
                <Switch size="small" checked={!!n.kata_capable} loading={saving === n.id}
                    onChange={(v) => save(n, { kata_capable: v ? 1 : 0 })} />
            ),
        },
        {
            title: t('Runtime'), key: 'rt', render: (_, n) => (
                <RuntimeSelect value={n.runtime} warning={warning} loading={saving === n.id} onChange={(v) => save(n, { runtime: v })} />
            ),
        },
        {
            title: t('Ready'), key: 'ready', render: (_, n) => (n.unready
                ? <Tooltip title={n.kata_reported_at || ''}><Tag color="warning">{n.unready}</Tag></Tooltip>
                : <Tag color="success">{t('Ready')}</Tag>),
        },
        {
            title: t('Gateway port'), key: 'gw', render: (_, n) => (
                <InputNumber size="small" min={1} max={65535} style={{ width: 100 }}
                    value={edits[n.id]?.gateway_port ?? n.gateway_port} onChange={(v) => edit(n.id, 'gateway_port', v)} />
            ),
        },
        {
            title: t('Private CIDR'), key: 'cidr', render: (_, n) => (
                <Input size="small" style={{ width: 150 }} value={edits[n.id]?.private_cidr ?? n.private_cidr ?? ''}
                    onChange={(e) => edit(n.id, 'private_cidr', e.target.value)} />
            ),
        },
        { title: t('Containers'), dataIndex: 'containers' },
        { title: t('Committed memory'), dataIndex: 'committed_mem_mb', render: (v) => (v ? `${v} MB` : '-') },
        {
            title: '', key: 'op', render: (_, n) => (edits[n.id]
                ? <Button size="small" type="primary" loading={saving === n.id} onClick={() => save(n, edits[n.id])}>{t('Save')}</Button>
                : null),
        },
    ];
    return (
        <>
            <Space style={{ marginBottom: 12 }}>
                <Button icon={<ReloadOutlined />} onClick={load} />
                {overhead !== null && <Text type="secondary">{t('Overhead per container')}: {overhead} MB</Text>}
            </Space>
            {rows.some((n) => n.runtime === 'runc') && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t(warning)} />}
            <Table rowKey="id" size="small" columns={columns} dataSource={rows} loading={loading} pagination={false} scroll={{ x: 'max-content' }} />
            {legacy.length > 0 && (
                <Table rowKey="id" size="small" style={{ marginTop: 16 }} pagination={false} dataSource={legacy} scroll={{ x: 'max-content' }}
                    title={() => <Space>{t('Legacy nodes')}<Tag>{t('Legacy')}</Tag></Space>}
                    columns={[
                        { title: t('Node'), key: 'n', render: (_, n) => <Space direction="vertical" size={0}><span>{n.name}</span><Text type="secondary" style={{ fontSize: 12 }}>{n.ip}</Text></Space> },
                        { title: t('Status'), dataIndex: 'status' },
                        { title: t('Runtime'), dataIndex: 'runtime' },
                        { title: t('Containers'), dataIndex: 'containers' },
                        { title: t('Last heartbeat'), dataIndex: 'last_heartbeat', render: (v) => v || '-' },
                    ]} />
            )}
        </>
    );
};

const PlansTab = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(null);
    const [warning, setWarning] = useState(RUNC_WARNING);
    const load = useCallback(() => {
        setLoading(true);
        api.plans().then((r) => {
            setLoading(false);
            if (isOk(r)) { setRows(arr(r.data?.list)); if (r.data?.runc_warning) setWarning(r.data.runc_warning); } else message.error(errMsg(r, t('Failed to load')));
        });
    }, [t]);
    useEffect(() => { load(); }, [load]);
    const setRuntime = async (p, runtime) => {
        setSaving(p.id);
        const r = await api.updatePlan(p.id, { runtime });
        setSaving(null);
        if (isOk(r)) { message.success(t('Saved')); load(); } else message.error(errMsg(r, t('Failed to save')));
    };
    const setWarp = async (p, warp) => {
        setSaving(p.id);
        const r = await api.updatePlan(p.id, { warp });
        setSaving(null);
        if (isOk(r)) { message.success(t('Saved')); load(); } else message.error(errMsg(r, t('Failed to save')));
    };
    const columns = [
        { title: 'ID', dataIndex: 'id', width: 70 },
        { title: t('Name'), dataIndex: 'name' },
        { title: t('Status'), dataIndex: 'status', render: (v) => <Tag color={v === 'active' ? 'success' : 'default'}>{v || '-'}</Tag> },
        { title: t('Spec'), key: 'spec', render: (_, p) => specText(p.spec || {}) },
        {
            title: <Tooltip title={t('Containers of this plan can use WARP egress once the node has WARP on')}>WARP</Tooltip>, key: 'warp', render: (_, p) => (
                p.is_addon ? '-' : <Switch size="small" checked={p.warp !== false} loading={saving === p.id} onChange={(v) => setWarp(p, v)} />
            ),
        },
        {
            title: t('Runtime'), key: 'rt', render: (_, p) => (
                p.is_addon ? '-' : <RuntimeSelect value={p.runtime} warning={warning} loading={saving === p.id} onChange={(v) => setRuntime(p, v)} />
            ),
        },
        {
            title: t('Price'), key: 'pricing', render: (_, p) => (
                <Space wrap size={4}>
                    {arr(p.pricing).map((c) => (
                        <Tag key={c.cycle} color={c.enabled ? 'blue' : 'default'}>{t(CYCLES[c.cycle] || c.cycle)} {c.price}</Tag>
                    ))}
                </Space>
            ),
        },
    ];
    return (
        <>
            <Text type="secondary" style={{ display: 'block', marginBottom: 12 }}>{t('Plans are managed in Product management')}</Text>
            {rows.some((p) => p.runtime === 'runc') && <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={t(warning)} />}
            <Table rowKey="id" size="small" columns={columns} dataSource={rows} loading={loading} pagination={false} />
        </>
    );
};

const SettingsTab = () => {
    const { t } = useTranslation();
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    useEffect(() => {
        api.settings().then((r) => {
            setLoading(false);
            if (isOk(r)) form.setFieldsValue({ port_limit: r.data?.port_limit }); else message.error(errMsg(r, t('Failed to load')));
        });
    }, [form, t]);
    const save = async (v) => {
        setSaving(true);
        const r = await api.settings({ port_limit: v.port_limit });
        setSaving(false);
        if (isOk(r)) { message.success(t('Saved')); form.setFieldsValue({ port_limit: r.data?.port_limit }); } else message.error(errMsg(r, t('Failed to save')));
    };
    return (
        <Form form={form} layout="inline" onFinish={save} disabled={loading}>
            <Form.Item name="port_limit" label={t('Port mapping limit')} rules={[{ required: true }]}
                tooltip={t('Global default; plans can override it with resource_features port_limit / max_disk_gb')}>
                <InputNumber min={0} max={200} style={{ width: 120 }} />
            </Form.Item>
            <Form.Item><Button type="primary" htmlType="submit" loading={saving}>{t('Save')}</Button></Form.Item>
        </Form>
    );
};

/** 已付款但没有节点能开通的订单：自动重试（先付先开），管理员可立即重试 / 指定节点 / 取消并退款。 */
const PendingTab = () => {
    const { t } = useTranslation();
    const [status, setStatus] = useState('pending');
    const [data, setData] = useState({ orders: [], plans: [], addons: [] });
    const [nodes, setNodes] = useState([]);
    const [loading, setLoading] = useState(false);
    const [busy, setBusy] = useState(null);
    const load = useCallback(async () => {
        setLoading(true);
        const [r, n] = await Promise.all([api.pending(status), api.nodes()]);
        setLoading(false);
        if (isOk(r)) setData({ orders: arr(r.data?.orders), plans: arr(r.data?.plans), addons: arr(r.data?.addons) });
        else message.error(errMsg(r, t('Failed to load')));
        if (isOk(n)) setNodes(arr(n.data?.list).filter((x) => x.kata_capable));
    }, [status, t]);
    useEffect(() => { load(); }, [load]);
    const act = async (key, fn, ok) => {
        setBusy(key);
        const r = await fn();
        setBusy(null);
        if (isOk(r)) { message.success(ok); load(); } else message.error(errMsg(r, t('Operation failed')));
    };
    const reasons = (list) => (arr(list).length ? (
        <Space direction="vertical" size={0}>
            {arr(list).map((x) => <Text key={`${x.node_id}-${x.reason}`} type="secondary" style={{ fontSize: 12 }}>#{x.node_id} {x.node}：{x.reason}</Text>)}
        </Space>
    ) : '-');
    const orderCols = [
        { title: t('Subscription'), dataIndex: 'subscription_id' },
        { title: t('User'), dataIndex: 'uid' },
        { title: t('Plan'), key: 'p', render: (_, o) => o.product || `#${o.product_id}` },
        { title: t('Region'), dataIndex: 'area_id' },
        { title: t('Queued since'), dataIndex: 'created_at' },
        { title: t('Count'), dataIndex: 'attempts' },
        { title: t('Node refusals'), key: 'tried', render: (_, o) => reasons(o.tried) },
        ...(status === 'pending' ? [] : [{ title: t('Result'), key: 'res', render: (_, o) => (o.container_id ? `#${o.container_id} @ ${o.node_id}` : o.status) }]),
        {
            title: '', key: 'op', render: (_, o) => (o.status !== 'pending' ? null : (
                <Space wrap>
                    <Button size="small" loading={busy === `r${o.subscription_id}`}
                        onClick={() => act(`r${o.subscription_id}`, () => api.retryPending(o.subscription_id), t('Enabled'))}>{t('Retry')}</Button>
                    <Select size="small" style={{ width: 150 }} placeholder={t('Pick node')} value={null}
                        options={nodes.map((n) => ({ value: n.id, label: `#${n.id} ${n.name}` }))}
                        onChange={(nid) => act(`r${o.subscription_id}`, () => api.retryPending(o.subscription_id, nid), t('Enabled'))} />
                    <Button size="small" danger loading={busy === `c${o.subscription_id}`}
                        onClick={() => Modal.confirm({ title: t('Cancel and refund?'), okButtonProps: { danger: true },
                            onOk: () => act(`c${o.subscription_id}`, () => api.cancelPending(o.subscription_id), t('Cancelled and refunded')) })}>{t('Cancel & refund')}</Button>
                </Space>
            )),
        },
    ];
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Space>
                <Select value={status} onChange={setStatus} style={{ width: 140 }} options={[
                    { value: 'pending', label: t('Pending provisioning') }, { value: 'placed', label: t('Enabled') },
                    { value: 'cancelled', label: t('Canceled') }, { value: 'all', label: t('All') }]} />
                <Button icon={<ReloadOutlined />} onClick={load} loading={loading} />
            </Space>
            <Table rowKey="subscription_id" size="small" columns={orderCols} dataSource={data.orders} loading={loading}
                scroll={{ x: 'max-content' }} pagination={data.orders.length > 20 ? { pageSize: 20 } : false} />
            {(data.plans.length > 0 || data.addons.length > 0) && (
                <Table size="small" title={() => t('Paid upgrades and add-ons waiting for capacity')} rowKey={(r) => r.key} pagination={false} scroll={{ x: 'max-content' }}
                    dataSource={[
                        ...data.plans.map((p) => ({ key: `p${p.container_id}`, container_id: p.container_id, node_id: p.node_id, what: `${t('Plan')} #${p.product_id}`, since: p.since, reason: p.error })),
                        ...data.addons.map((a) => ({ key: `a${a.item_id}`, item_id: a.item_id, container_id: a.container_id, what: a.addon === 'ctr_disk' ? `${t('Disk')} +${a.gb} GB` : t('Public IP'), since: a.since, reason: a.reason })),
                    ]}
                    columns={[
                        { title: t('Container'), dataIndex: 'container_id' },
                        { title: t('Content'), dataIndex: 'what' },
                        { title: t('Queued since'), dataIndex: 'since' },
                        { title: t('Reason'), dataIndex: 'reason' },
                        {
                            title: '', key: 'op', render: (_, r) => (r.item_id ? (
                                <Button size="small" danger onClick={() => Modal.confirm({ title: t('Cancel and refund?'), okButtonProps: { danger: true },
                                    onOk: () => act(`a${r.item_id}`, () => api.cancelPendingAddon(r.item_id), t('Cancelled and refunded')) })}>{t('Cancel & refund')}</Button>
                            ) : null),
                        },
                    ]} />
            )}
        </Space>
    );
};

const TemplatesTab = () => {
    const { t } = useTranslation();
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(false);
    useEffect(() => {
        setLoading(true);
        api.templates().then((r) => {
            setLoading(false);
            if (isOk(r)) setRows(arr(r.data?.list)); else message.error(errMsg(r, t('Failed to load')));
        });
    }, [t]);
    return (
        <Table rowKey="key" size="small" dataSource={rows} loading={loading} pagination={false} scroll={{ x: 'max-content' }}
            columns={[
                { title: 'Key', dataIndex: 'key' },
                { title: t('Name'), dataIndex: 'name' },
                { title: t('Image'), dataIndex: 'image', render: (v) => <Text code>{v}</Text> },
                { title: t('HTTP port'), dataIndex: 'http_port', render: (v) => v || '-' },
                { title: t('Files root'), dataIndex: 'files_root', render: (v) => v || '-' },
            ]} />
    );
};

// ------------------------------------------------------------------ Cloudflare WARP

const fmtBytes = (n) => {
    const v = Number(n || 0);
    if (v >= 1e12) return `${(v / 1e12).toFixed(2)} TB`;
    if (v >= 1e9) return `${(v / 1e9).toFixed(2)} GB`;
    if (v >= 1e6) return `${(v / 1e6).toFixed(1)} MB`;
    return `${Math.round(v / 1e3)} KB`;
};
const ACCOUNT = { free: ['免费', 'default'], limited: ['WARP+', 'blue'], unlimited: ['WARP+ 无限', 'gold'], team: ['Zero Trust', 'purple'] };

const WarpStatus = ({ n }) => {
    const { t } = useTranslation();
    const s = n.status || {};
    if (!n.enabled) return <Text type="secondary">-</Text>;
    if (!n.capable) return <Tooltip title={t('Node agent needs an upgrade')}><Tag>{t('Not supported')}</Tag></Tooltip>;
    const err = s.api_error || s.error;
    const tag = s.healthy ? <Tag color="success">{t('Normal')}</Tag> : s.up ? <Tag color="error">{t('Down')}</Tag> : <Tag color="warning">{t('Starting')}</Tag>;
    return (
        <Space direction="vertical" size={0}>
            <Space size={4}>{err ? <Tooltip title={err}>{tag}</Tooltip> : tag}
                {s.handshake_age >= 0 && <Text type="secondary" style={{ fontSize: 12 }}>{t('Handshake {{s}}s ago', { s: s.handshake_age })}</Text>}</Space>
            {(s.rx_bytes > 0 || s.tx_bytes > 0) && <Text type="secondary" style={{ fontSize: 12 }}>↓{fmtBytes(s.rx_bytes)} ↑{fmtBytes(s.tx_bytes)}</Text>}
        </Space>
    );
};

const WarpTab = () => {
    const { t } = useTranslation();
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(null);
    const [form] = Form.useForm();
    const load = useCallback(async () => {
        setLoading(true);
        const r = await api.warp();
        setLoading(false);
        if (isOk(r)) setData(r.data || {}); else message.error(errMsg(r, t('Failed to load')));
    }, [t]);
    useEffect(() => { load(); }, [load]);
    const act = async (key, p) => {
        setSaving(key);
        const r = await p;
        setSaving(null);
        if (isOk(r)) { message.success(t('Saved')); load(); } else message.error(errMsg(r, t('Failed to save')));
        return isOk(r);
    };
    const licenses = arr(data?.licenses);
    const licOptions = [{ value: 0, label: t('Free') }, ...licenses.filter((l) => l.status === 'active')
        .map((l) => ({ value: l.id, label: `${l.label || 'WARP+'} …${l.hint} (${l.used}/${l.max_devices})` }))];

    const nodeCols = [
        { title: t('Node'), key: 'n', render: (_, n) => <Space direction="vertical" size={0}><span>{n.node_name}</span><Text type="secondary" style={{ fontSize: 12 }}>#{n.node_id}</Text></Space> },
        { title: t('Enable'), key: 'en', render: (_, n) => <Switch size="small" checked={n.enabled} loading={saving === `n${n.node_id}`} onChange={(v) => act(`n${n.node_id}`, api.warpNode(n.node_id, { enabled: v }))} /> },
        { title: t('Status'), key: 'st', render: (_, n) => <WarpStatus n={n} /> },
        {
            title: t('Egress IP'), key: 'ip', render: (_, n) => {
                const s = n.status || {};
                const ips = [s.exit_ip4, s.exit_ip6].filter(Boolean);
                return ips.length ? <Space direction="vertical" size={0}>{ips.map((ip) => <Text key={ip} code style={{ fontSize: 12 }}>{ip}</Text>)}</Space> : '-';
            },
        },
        {
            title: t('Account'), key: 'acc', render: (_, n) => {
                const a = n.status?.account;
                if (!a) return '-';
                const [label, color] = ACCOUNT[a.account_type] || [a.account_type, 'default'];
                const quota = a.account_type === 'limited' && a.premium_data > 0 ? `${fmtBytes(a.premium_data)}` : null;
                return <Space size={4}><Tag color={color}>{t(label)}</Tag>{quota && <Tooltip title={t('Data left')}><Text type="secondary" style={{ fontSize: 12 }}>{quota}</Text></Tooltip>}</Space>;
            },
        },
        {
            title: <Tooltip title={t('A WARP+ license bound to this node\'s WARP device')}>{t('License')}</Tooltip>, key: 'lic', render: (_, n) => (
                <Space size={4}>
                    <Select size="small" style={{ width: 210 }} value={n.license_id || 0} options={licOptions} loading={saving === `l${n.node_id}`}
                        onChange={(v) => act(`l${n.node_id}`, api.warpNode(n.node_id, { license_id: v }))} />
                    {n.enabled && n.capable && !n.license_bound && <Tooltip title={n.status?.api_error || t('Binding')}><Tag color="processing">{t('Binding')}</Tag></Tooltip>}
                </Space>
            ),
        },
        { title: t('Container'), dataIndex: 'containers', width: 70 },
        {
            title: '', key: 'op', render: (_, n) => (n.device_id ? (
                <Popconfirm title={t('Register a new WARP device and replace the current one?')} onConfirm={() => act(`r${n.node_id}`, api.warpNode(n.node_id, { reregister: true }))}>
                    <Tooltip title={n.device_id}><Button size="small" loading={saving === `r${n.node_id}`} disabled={n.reregister}>{t('Replace device')}</Button></Tooltip>
                </Popconfirm>
            ) : null),
        },
    ];
    const licCols = [
        { title: t('Name'), dataIndex: 'label', render: (v, l) => <Space size={4}>{v || '-'}<Text type="secondary">…{l.hint}</Text></Space> },
        {
            title: t('Device'), key: 'dev', render: (_, l) => (
                <Tooltip title={l.devices_seen !== null ? t('WARP account shows {{n}} devices', { n: l.devices_seen }) : undefined}>
                    <Text type={l.used >= l.max_devices ? 'danger' : undefined}>{l.used}/{l.max_devices}</Text>
                </Tooltip>
            ),
        },
        { title: t('Account'), dataIndex: 'account_type', render: (v) => (v ? <Tag color={(ACCOUNT[v] || [])[1]}>{t((ACCOUNT[v] || [v])[0])}</Tag> : '-') },
        { title: t('Enable'), key: 'st', render: (_, l) => <Switch size="small" checked={l.status === 'active'} loading={saving === `s${l.id}`} onChange={(v) => act(`s${l.id}`, api.warpUpdateLicense(l.id, { status: v ? 'active' : 'disabled' }))} /> },
        {
            title: '', key: 'op', render: (_, l) => (
                <Popconfirm title={t('Delete it?')} okType="danger" disabled={l.used > 0} onConfirm={() => act(`d${l.id}`, api.warpDeleteLicense(l.id))}>
                    <Button size="small" danger type="link" disabled={l.used > 0}>{t('Delete')}</Button>
                </Popconfirm>
            ),
        },
    ];
    const add = async (v) => {
        if (await act('add', api.warpAddLicense({ label: v.label || '', license: v.license, max_devices: v.max_devices }))) form.resetFields();
    };

    if (data && data.available === false) return <Alert type="info" showIcon message={t('Not yet available')} />;
    return (
        <Space direction="vertical" size={16} style={{ width: '100%' }}>
            <Table rowKey="node_id" size="small" columns={nodeCols} dataSource={arr(data?.nodes)} loading={loading} pagination={false} scroll={{ x: true }} />
            <Card size="small" title={<Tooltip title={t('Each license covers a limited number of devices; one node uses one')}>{t('WARP+ licenses')}</Tooltip>}>
                <Form form={form} layout="inline" onFinish={add} initialValues={{ max_devices: data?.default_max_devices || 5 }} style={{ marginBottom: 12, rowGap: 8 }}>
                    <Form.Item name="label"><Input placeholder={t('Name')} style={{ width: 140 }} maxLength={64} /></Form.Item>
                    <Form.Item name="license" rules={[{ required: true, message: t('Enter the license key') }]}>
                        <Input.Password placeholder={t('License key')} style={{ width: 260 }} autoComplete="off" />
                    </Form.Item>
                    <Form.Item name="max_devices" tooltip={t('Device limit')}><InputNumber min={1} max={100} style={{ width: 80 }} /></Form.Item>
                    <Form.Item><Button htmlType="submit" loading={saving === 'add'}>{t('Add')}</Button></Form.Item>
                </Form>
                <Table rowKey="id" size="small" columns={licCols} dataSource={licenses} pagination={false} />
            </Card>
        </Space>
    );
};

const TABS = ['list', 'nodes', 'plans', 'templates', 'pending', 'warp', 'settings'];

const ContainerManagement = () => {
    const { t } = useTranslation();
    const { tab } = useParams();
    const navigate = useNavigate();
    const active = TABS.includes(tab) ? tab : 'list';
    return (
        <div>
            <PageHeader title={t('Dedicated Containers')} />
            <Card styles={{ body: { paddingTop: 4 } }}>
                <Tabs destroyInactiveTabPane activeKey={active} onChange={(k) => navigate(adminPath(`/evm/containers/${k}`))} items={[
                    { key: 'list', label: t('Instances'), children: <InstancesTab /> },
                    { key: 'nodes', label: t('Node'), children: <NodesTab /> },
                    { key: 'plans', label: t('Plan'), children: <PlansTab /> },
                    { key: 'templates', label: t('Template'), children: <TemplatesTab /> },
                    { key: 'pending', label: t('Pending provisioning'), children: <PendingTab /> },
                    { key: 'warp', label: 'WARP', children: <WarpTab /> },
                    { key: 'settings', label: t('Settings'), children: <SettingsTab /> },
                ]} />
            </Card>
        </div>
    );
};

export default ContainerManagement;
